/**
 * IM-003: Resolve warehouse inventory from geolocation when the inventory cookie is empty.
 *
 * Flow (client-only, silent on failure):
 * 1. Shared client Google Geolocation + reverse geocode (deduped with region redirect / Canada locale).
 * 2. POST /server/shipping/get-methods with empty cart (same orchestration as IM-002).
 * 3. On SHIPPING_API / HYBRID with WhsCode, reuse useWarehouseInventory → getStores + cookies.
 *
 * Does not prompt navigator.geolocation on load (UX).
 */

import {useEffect, useRef, useState} from 'react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {resolveBrowserGeoFull} from '@amblique/king-living/app/utils/browser-geo-address';
import {useGeocoding} from './use-geocoding';
import {useWarehouseInventory} from './use-warehouse-inventory';
import {
  getWarehouseInventoryListIdFromCookie,
  isWarehouseResolutionPreferenceEnabled,
} from '../utils/warehouse-inventory-cookies';
import {
  getWhsCodeFromShippingMethod,
  isShippingServiceApiSource,
} from '../utils/shipping-method-whs-code';

/**
 * @param {Object} options
 * @param {boolean} [options.enabled]
 */
export function useWarehouseInventoryOnLoad({enabled = true} = {}) {
  const {site} = useMultiSite();
  const siteId = site?.id || 'kl-au';
  const geoApiKey = site?.preferences?.GOOGLE_GEOLOCATION_BROWSER_API_KEY;
  const warehousePrefSignature = String(site?.preferences?.warehouseInventories ?? '');
  const prevSiteIdRef = useRef(null);
  const reverseGeocodeRef = useRef(null);

  const {reverseGeocode} = useGeocoding();

  useEffect(() => {
    reverseGeocodeRef.current = reverseGeocode;
  }, [reverseGeocode]);

  const [bootstrap, setBootstrap] = useState({
    whs: null,
    addr: null,
    api: false,
  });

  useEffect(() => {
    if (prevSiteIdRef.current != null && prevSiteIdRef.current !== siteId) {
      setBootstrap({whs: null, addr: null, api: false});
    }

    prevSiteIdRef.current = siteId;
  }, [siteId]);

  useWarehouseInventory({
    whsCode: bootstrap.whs || undefined,
    address: bootstrap.addr,
    shippingApiActive: bootstrap.api,
    enabled: enabled && Boolean(bootstrap.whs),
  });

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    if (!isWarehouseResolutionPreferenceEnabled({warehouseInventories: warehousePrefSignature})) {
      return undefined;
    }

    const existing = getWarehouseInventoryListIdFromCookie(siteId);

    if (existing != null && String(existing).trim() !== '') {
      return undefined;
    }

    if (!geoApiKey) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const geo = await resolveBrowserGeoFull({
          siteId,
          apiKey: geoApiKey,
          reverseGeocode: reverseGeocodeRef.current,
        });

        if (cancelled) {
          return;
        }

        if (!geo.postcode || !geo.addressCountryCode) {
          return;
        }

        const addressPayload = {
          postcode: geo.postcode,
          countryCode: geo.addressCountryCode,
          stateCode: geo.stateCode || undefined,
        };

        const res = await fetch('/server/shipping/get-methods', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            siteId,
            address: addressPayload,
            cartItems: [],
            sfccMethods: null,
          }),
        });

        if (cancelled || !res.ok) {
          return;
        }

        const data = await res.json().catch(() => null);

        if (cancelled || !data?.methods?.length) {
          return;
        }

        const source = data.source;
        const apiPath = isShippingServiceApiSource(source);

        if (!apiPath) {
          return;
        }

        const withWhs = data.methods.find((m) => getWhsCodeFromShippingMethod(m));
        const whs = getWhsCodeFromShippingMethod(withWhs);

        if (!whs) {
          return;
        }

        const addrForCookie = {
          postalCode: geo.postcode,
          stateCode: geo.stateCode || '',
          countryCode: geo.addressCountryCode,
        };

        if (!cancelled) {
          setBootstrap({whs, addr: addrForCookie, api: true});
        }
      } catch {
        // Silent failure per TSD (no shopper-facing error)
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, geoApiKey, siteId, warehousePrefSignature]);
}

export default useWarehouseInventoryOnLoad;
